/*
PURPOSE: Derive the checkout fee preview from a pledge amount, validating that amount first.

USE CASES:
- The checkout page, which takes the pledge from a query parameter and must not hand an
  unchecked value to the fee producer

DOMAIN BOUNDARIES:
- Validation and a single call into the shared contribution-fee producer. No fee arithmetic of
  its own: the split has exactly one source of truth, and duplicating it here is the defect this
  file exists to remove.

ASSUMPTIONS:
- The pledge arrives as dollars and may be anything at all, including absent, fractional beyond
  a cent, out of range, or not a number
- The producer rejects malformed input by throwing, so an unvalidated parameter would surface as
  a thrown error on the page rather than a wrong figure. Validation happens here, at the boundary
*/

import {
  computeContributionFeeBreakdown,
  type ContributionFeeBreakdown,
} from 'shared/utils/coralContributionFee';

/** The supported contribution range, matching the member slider. */
export const MIN_CONTRIBUTION_CENTS = 400;
export const MAX_CONTRIBUTION_CENTS = 7000;

/**
 * Narrow this with an explicit `preview.ok === false`, not a bare `!preview.ok`. The test-tree
 * typecheck (tsconfig.tests.json) compiles with `strict: false`, and with strictNullChecks off a
 * boolean-literal discriminant does not narrow on truthiness — only on an explicit comparison.
 */
export type PledgePreview =
  | { ok: true; breakdown: ContributionFeeBreakdown }
  | { ok: false; reason: string };

/**
 * Validate a pledge amount in dollars and return its fee breakdown, or a reason it was refused.
 * Refusal is a returned value rather than a throw so the page can render a message instead of
 * breaking.
 */
export function pledgePreview(pledgeAmountDollars: number): PledgePreview {
  if (!Number.isFinite(pledgeAmountDollars)) {
    return { ok: false, reason: 'That contribution amount is not a number.' };
  }

  const cents = Math.round(pledgeAmountDollars * 100);

  // Reject rather than silently rounding: a sub-cent amount means the caller sent something we
  // were not meant to charge.
  //
  // Compare in DOLLARS against the rounded value. Comparing the scaled product against
  // Number.EPSILON does not work: that epsilon is relative to 1.0, so at these magnitudes
  // ordinary float noise (~5.7e-14 for a value like 4.10) exceeds it and legitimate
  // two-decimal amounts get rejected. The tolerance below sits far above that noise and far
  // below a tenth of a cent.
  const SUB_CENT_TOLERANCE = 1e-9;
  if (Math.abs(pledgeAmountDollars - cents / 100) > SUB_CENT_TOLERANCE) {
    return { ok: false, reason: 'That contribution amount includes a fraction of a cent.' };
  }

  if (cents < MIN_CONTRIBUTION_CENTS || cents > MAX_CONTRIBUTION_CENTS) {
    const min = (MIN_CONTRIBUTION_CENTS / 100).toFixed(2);
    const max = (MAX_CONTRIBUTION_CENTS / 100).toFixed(2);
    return { ok: false, reason: `Contributions run from $${min} to $${max} a month.` };
  }

  return { ok: true, breakdown: computeContributionFeeBreakdown(cents) };
}
